import React, { useEffect } from 'react';

import { useQuery } from '@edreamsodigeo/frontend-api-client';
import { useSearchQuery } from '../SearchRequestProvider';
import { ITINERARY_SEARCH_QUERY } from '../operations/itinerarySearch';
import {
  useLoadingResultsPage,
  useTrackErrors,
  useUpdateMeshTransactionId,
} from './hooks';
import FrontendAPIErrorManager from 'clientState/errorManagement';
import {
  ItinerarySearchQuery,
  SearchQueryQueryVariables,
  SearchRequest,
} from '../../../generated/graphql';
import { useEndSearchMetricEffect } from '../SearchTrackerProvider';
import { useFrontendApiBlockingError } from '@edreamsodigeo/frontend-core';
import { transformSuccessQuery } from '../transformSuccessQuery';
import { transformItinerarySearchResponse } from './helpers/transformItinerarySearchResponse';
import { Extensions } from '../../resolvers/itinerary';
import {
  SearchResponseContext,
  SearchResponseActionsContext,
  replaceItinerariesById,
} from './index';
import { SearchResponseProviderProps } from './types';
import { useAlternativeItineraryMutation } from 'clientState/providers/AlternativeItineraryMutationProvider';
import { useHash } from 'clientState/hooks/useUrlParams';
import {
  useFrontendApiValidationError,
  VALIDATION_SEARCH_ERROR,
} from 'clientState/hooks/useFrontendApiValidationError/useFrontendApiValidationError';

const ItinerarySearchResponseNoShoppingCartProvider: React.FC<
  SearchResponseProviderProps
> = ({
  onNoSearchResults,
  onSearchParamsError,
  children,
  onSearchMapiError,
  onLoadingResultsPage,
}) => {
  const searchRequest = useSearchQuery();
  const {
    alternativeMutationLaunched,
    alternativeSearchId,
    alternativeItineraries,
  } = useAlternativeItineraryMutation();
  const hash = useHash();
  const skipQuery =
    searchRequest === null ||
    alternativeMutationLaunched ||
    alternativeSearchId !== null ||
    !!(hash && !(hash.includes('#results') || hash.includes('#/results')));
  const queryItinerarySearchResult = useQuery<
    ItinerarySearchQuery,
    SearchQueryQueryVariables
  >(ITINERARY_SEARCH_QUERY, {
    variables: {
      searchRequest: searchRequest as SearchRequest,
    },
    skip: skipQuery,
    notifyOnBackgroundFetching: true,
  });
  // Handles any broken flow error from the back-end, but not the validation
  // errors
  useFrontendApiBlockingError(queryItinerarySearchResult, [
    VALIDATION_SEARCH_ERROR,
  ]);
  // Handles the validation errors
  useFrontendApiValidationError(queryItinerarySearchResult);

  const itineraries =
    queryItinerarySearchResult.data?.itinerarySearch?.itineraries || [];
  const itinerariesParsed = replaceItinerariesById(
    itineraries,
    alternativeItineraries
  );

  if (queryItinerarySearchResult.data) {
    queryItinerarySearchResult.data.itinerarySearch.itineraries =
      itinerariesParsed;
  }

  const searchQueryState = transformSuccessQuery(
    {
      loading: queryItinerarySearchResult.loading,
      error: queryItinerarySearchResult.errors,
      data: queryItinerarySearchResult.data,
      extensions: queryItinerarySearchResult.extensions,
    },
    transformItinerarySearchResponse,
    {}
  );
  const { loading: loadingSearch, data, error } = searchQueryState;

  useEffect(() => {
    if (
      data &&
      searchRequest?.itinerary?.externalSelection &&
      !data.search.externalSelection
    ) {
      onSearchMapiError();
    }
  }, [data, onSearchMapiError, searchRequest]);

  const loading = loadingSearch || searchRequest === null;
  const extensions = searchQueryState?.extensions;

  useEndSearchMetricEffect(queryItinerarySearchResult);
  useTrackErrors(data, loading, queryItinerarySearchResult.errors);
  useUpdateMeshTransactionId(data);
  useLoadingResultsPage(loading, onLoadingResultsPage);

  const nextQueryState = React.useMemo(
    () => ({
      loading,
      data: loading ? null : data,
      error: error,
      extensions: extensions as Extensions | null,
    }),
    [loading, data, error, extensions]
  );

  const actions = {
    onNoSearchResults,
    onSearchParamsError,
  };

  return (
    <>
      <FrontendAPIErrorManager
        errors={queryItinerarySearchResult.errors}
        id="SearchResponseProvider"
      />
      <SearchResponseContext.Provider value={nextQueryState}>
        <SearchResponseActionsContext.Provider value={actions}>
          {children}
        </SearchResponseActionsContext.Provider>
      </SearchResponseContext.Provider>
    </>
  );
};

export default ItinerarySearchResponseNoShoppingCartProvider;
